import { createServerFn } from "@tanstack/react-start";
import { and, asc, desc, eq, isNull } from "drizzle-orm";
import { z } from "zod";
import { nanoid } from "nanoid";
import { generateText } from "ai";
import { authMiddleware } from "~/lib/middlewares/auth-middleware";
import { Database } from "~/lib/server/database";
import { mainChatModel, renderPrompt, titleModel } from "~/lib/server/ai";
import {
	chatConversations,
	chatMessages,
	type ChatMode,
} from "~/lib/server/db/schema/chat-schema";
import { Effect, Schedule } from "effect";
import { ChatNotFoundError, NotAuthenticatedError } from "../error";
import generateTitlePrompt from "~/data/prompts/utils/generate-title.md?raw";
import generateStudyMaterialPrompt from "~/data/prompts/utils/generate-study-material.md?raw";

export const getChatSessionsList = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.validator(z.object({ mode: z.string().optional() }))
	.handler(({ data, context }) =>
		Effect.gen(function* () {
			const db = yield* Database;

			const { user } = context.auth;
			if (!user) {
				return yield* Effect.fail(new NotAuthenticatedError());
			}

			const whereConditions = [
				eq(chatConversations.uid, user.id),
				isNull(chatConversations.deleted_at),
			];

			if (data.mode) {
				whereConditions.push(eq(chatConversations.mode, data.mode as ChatMode));
			}

			const userConversations = yield* Effect.tryPromise(() =>
				db
					.select({
						id: chatConversations.id,
						title: chatConversations.title,
						mode: chatConversations.mode,
						createdAt: chatConversations.created_at,
						updatedAt: chatConversations.updated_at,
					})
					.from(chatConversations)
					.where(and(...whereConditions))
					.orderBy(desc(chatConversations.updated_at))
					.execute(),
			);

			return userConversations;
		}).pipe(
			Effect.withSpan("get-chat-sessions-list"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);

export const createChatSession = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.validator(
		z.object({
			initialMessageContent: z.string().min(1),
			mode: z.string().default("regular"),
		}),
	)
	.handler(({ data, context }) =>
		Effect.gen(function* () {
			const db = yield* Database;

			const { user } = context.auth;
			if (!user) {
				return yield* Effect.fail(new NotAuthenticatedError());
			}

			// use user message as temporary title because AI result might not be available
			// immediately to us
			const temporaryTitle =
				data.initialMessageContent.substring(0, 50) +
				(data.initialMessageContent.length > 50 ? "..." : "");
			const newSessionId = nanoid();
			const now = new Date();

			const newSession = {
				id: newSessionId,
				uid: user.id,
				title: temporaryTitle,
				mode: data.mode as ChatMode,
				created_at: now,
				updated_at: now,
			};

			yield* Effect.tryPromise(() =>
				db.insert(chatConversations).values(newSession),
			);

			// schedule title generation in the background after we create the session
			Effect.retry(
				Effect.gen(function* () {
					const title = yield* Effect.tryPromise({
						try: () =>
							generateText({
								model: titleModel,
								prompt: renderPrompt(generateTitlePrompt, {
									message: data.initialMessageContent,
								}),
								temperature: 0.3,
							}),
						catch: (error) => {
							Effect.logError("Title generation failed", error);
							// use temporary title if generation fails
							return Effect.succeed(temporaryTitle);
						},
					}).pipe(
						Effect.andThen((res) => res.text.replace(/['"`]/g, "").trim()),
					);
					yield* Effect.tryPromise(() =>
						db
							.update(chatConversations)
							.set({ title })
							.where(eq(chatConversations.id, newSessionId)),
					);
				}).pipe(Effect.withSpan("chat-title-generation")),
				Schedule.intersect(
					Schedule.exponential("1 second"),
					Schedule.recurs(3),
				),
			).pipe(Effect.provide(Database.Default), Effect.runPromise);

			return {
				id: newSessionId,
				title: newSession.title,
				mode: newSession.mode,
				createdAt: newSession.created_at.toISOString(),
				updatedAt: newSession.updated_at.toISOString(),
			};
		}).pipe(
			Effect.withSpan("createChatSession"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);

export const updateChatSessionTitle = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.validator(
		z.object({
			id: z.string(),
			title: z.string().min(1).max(100),
		}),
	)
	.handler(({ data, context }) =>
		Effect.gen(function* () {
			const db = yield* Database;
			const { user } = context.auth;
			if (!user) {
				return yield* Effect.fail(new NotAuthenticatedError());
			}

			const { id, title } = data;

			const updateValues = {
				title,
				updated_at: new Date(),
			};

			const [updatedSession] = yield* Effect.tryPromise(() =>
				db
					.update(chatConversations)
					.set(updateValues)
					.where(
						and(
							eq(chatConversations.id, id),
							eq(chatConversations.uid, user.id),
						),
					)
					.returning({
						id: chatConversations.id,
						title: chatConversations.title,
						createdAt: chatConversations.created_at,
						updatedAt: chatConversations.updated_at,
					})
					.execute(),
			);

			if (!updatedSession) {
				return yield* Effect.fail(new ChatNotFoundError());
			}

			return {
				id: updatedSession.id,
				title: updatedSession.title,
				createdAt: updatedSession.createdAt?.toISOString() ?? "",
				updatedAt: updatedSession.updatedAt?.toISOString() ?? "",
			};
		}).pipe(
			Effect.withSpan("updateChatSessionTitle"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);

export const deleteChatSessionPermanently = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.validator(z.object({ id: z.string() }))
	.handler(({ data, context }) =>
		Effect.gen(function* () {
			const db = yield* Database;
			const { user } = context.auth;
			if (!user) {
				return yield* Effect.fail(new NotAuthenticatedError());
			}

			const conversation = yield* Effect.tryPromise(() =>
				db.query.chatConversations.findFirst({
					where: and(
						eq(chatConversations.id, data.id),
						eq(chatConversations.uid, user.id),
					),
				}),
			);

			if (!conversation) {
				return yield* Effect.fail(new ChatNotFoundError());
			}

			yield* Effect.tryPromise(() =>
				db.transaction(async (tx) => {
					await tx
						.delete(chatMessages)
						.where(eq(chatMessages.conversationId, data.id));
					await tx
						.delete(chatConversations)
						.where(eq(chatConversations.id, data.id));
				}),
			);

			return {
				success: true,
				deletedConversationId: data.id,
			};
		}).pipe(
			Effect.withSpan("deleteChatSessionPermanently"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);

export const getChatMessagesBySessionId = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.validator(z.object({ id: z.string() }))
	.handler(({ data, context }) =>
		Effect.gen(function* () {
			const db = yield* Database;
			const { user } = context.auth;
			if (!user) {
				return yield* Effect.fail(new NotAuthenticatedError());
			}

			const messages = yield* Effect.tryPromise(() =>
				db
					.select({
						id: chatMessages.id,
						content: chatMessages.content,
						role: chatMessages.role,
					})
					.from(chatMessages)
					.where(
						and(
							eq(chatMessages.conversationId, data.id),
							eq(chatMessages.uid, user.id),
						),
					)
					.orderBy(asc(chatMessages.created_at)),
			);

			return messages.map((msg) => ({
				id: msg.id,
				content: msg.content,
				role: msg.role as "user" | "assistant" | "system" | "data",
			}));
		}).pipe(
			Effect.withSpan("getChatMessagesBySessionId"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);

export const generateStudyMaterialsFromConversation = createServerFn({
	method: "POST",
})
	.middleware([authMiddleware])
	.validator(z.object({ conversationId: z.string() }))
	.handler(({ data, context }) =>
		Effect.gen(function* () {
			const db = yield* Database;
			const { user } = context.auth;
			if (!user) {
				return yield* Effect.fail(new NotAuthenticatedError());
			}

			const messages = yield* Effect.tryPromise(() =>
				db
					.select({
						content: chatMessages.content,
						role: chatMessages.role,
					})
					.from(chatMessages)
					.where(
						and(
							eq(chatMessages.conversationId, data.conversationId),
							eq(chatMessages.uid, user.id),
						),
					)
					.orderBy(asc(chatMessages.created_at)),
			).pipe(
				Effect.andThen((messages) =>
					messages.map((m) => `${m.role}: ${m.content}`).join("\n"),
				),
			);

			const { text: studyMaterials } = yield* Effect.tryPromise({
				try: () =>
					generateText({
						model: mainChatModel,
						prompt: renderPrompt(generateStudyMaterialPrompt, {
							conversation: messages,
						}),
						temperature: 0.5,
					}),
				catch: (error) => {
					Effect.logError("Study materials generation failed", error);
					return Effect.succeed({
						text: "Sorry, I failed to generate study materials.",
					});
				},
			});

			return { studyMaterials };
		}).pipe(
			Effect.withSpan("generateStudyMaterialsFromConversation"),
			Effect.provide(Database.Default),
			Effect.runPromise,
		),
	);
